import React, { useEffect, useState } from "react";
import MainLayout from "../../layouts/MainLayout";
import useAuth from "../../hooks/useAuth";
import { getDashboardInfo } from "../../api/dashboardApi";
import { getInstituteProfile } from "../../api/instituteApi";
import { getSmsBalance } from "../../api/smsApi";
import { fetchFileBlobUrl } from "../../api/fileApi";
import "./HomePage.css";

/**
 * Direct port of digicampusbd's real `/home` page (`home.html`) — the
 * literal post-login redirect target, distinct from the sidebar's own
 * separate "dashboard" page (`Dashboard.js`, ported in Phase 11/12). Real,
 * session-backed content: institute profile (name/address/id/academic
 * year/SMS balance), the logged-in user's own profile (photo/name/staff
 * id/designation/mobile/email/login id), and the same staff gender/HRM
 * counts `Dashboard.js` already fetches via `GET /api/dashboard`. The 4
 * bottom info tiles (Customer Support/Support Time/Masking SMS/Tutorial)
 * are hardcoded static text in the old template too (no `th:text` binding,
 * no AJAX call populates them) — reproduced verbatim rather than invented,
 * recolored to the brand navy instead of the old page's rainbow pastels.
 */
export default function HomePage() {
  const { user } = useAuth();
  const [profile, setProfile] = useState(null);
  const [info, setInfo] = useState(null);
  const [smsBalance, setSmsBalance] = useState(null);
  const [photoUrl, setPhotoUrl] = useState(null);
  const [logoUrl, setLogoUrl] = useState(null);

  useEffect(() => {
    getInstituteProfile().then(setProfile);
    getDashboardInfo().then(setInfo);
    getSmsBalance()
      .then((b) => setSmsBalance(b.balance))
      .catch(() => setSmsBalance(null));
  }, []);

  useEffect(() => {
    let objectUrl;
    let cancelled = false;
    if (profile && profile.imageName) {
      fetchFileBlobUrl("institute", profile.imageName)
        .then((url) => {
          if (!cancelled) {
            objectUrl = url;
            setLogoUrl(url);
          }
        })
        .catch(() => {
          if (!cancelled) setLogoUrl(null);
        });
    }
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [profile]);

  useEffect(() => {
    let objectUrl;
    let cancelled = false;
    if (user && user.imageName) {
      fetchFileBlobUrl("staff", user.imageName)
        .then((url) => {
          if (!cancelled) {
            objectUrl = url;
            setPhotoUrl(url);
          }
        })
        // The photo may have been uploaded through the old app (whose file
        // storage this app doesn't share) rather than re-uploaded here —
        // fall back to the placeholder instead of an unhandled rejection.
        .catch(() => {
          if (!cancelled) setPhotoUrl(null);
        });
    }
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [user]);

  return (
    <MainLayout>
      <div className="home-cards">
        <div className="home-card">
          <div className="home-card-main">
            <h2 className="home-institute-name">{profile ? profile.instituteName : "…"}</h2>
            <p className="home-institute-address">{profile ? profile.address : ""}</p>
            <div className="home-badge">
              {logoUrl ? (
                <img src={logoUrl} alt="" className="home-badge-logo" />
              ) : (
                <span className="home-badge-initial">{profile ? profile.instituteName.charAt(0) : ""}</span>
              )}
            </div>
          </div>
          <div className="home-card-stats">
            <StatBox value={profile ? profile.instituteId : "—"} label="Institute ID" />
            <StatBox value={profile ? profile.academicYear : "—"} label="Academic Year" />
            <StatBox value={smsBalance === null ? "—" : smsBalance} label="SMS Balance" />
          </div>
        </div>

        <div className="home-card">
          <div className="home-card-main">
            <div className="home-photo">
              {photoUrl ? <img src={photoUrl} alt="" /> : <span className="home-photo-placeholder">No photo</span>}
            </div>
            <dl className="home-user-info">
              <div>
                <dt>Name</dt>
                <dd>{user ? user.nickName : ""}</dd>
              </div>
              <div>
                <dt>Staff ID</dt>
                <dd>{user ? user.customStaffId : ""}</dd>
              </div>
              <div>
                <dt>Designation</dt>
                <dd>{user ? user.designation : ""}</dd>
              </div>
              <div>
                <dt>Mobile No.</dt>
                <dd>{user ? user.mobileNumber : ""}</dd>
              </div>
              <div>
                <dt>Email</dt>
                <dd>{user ? user.email : ""}</dd>
              </div>
              <div>
                <dt>Login ID</dt>
                <dd>{user ? user.username : ""}</dd>
              </div>
            </dl>
          </div>
          <div className="home-card-stats">
            <StatBox value={info ? info.totalMaleHr : "—"} label="Male" />
            <StatBox value={info ? info.totalFemaleHr : "—"} label="Female" />
            <StatBox value={info ? info.totalHr : "—"} label="Total HRM" />
          </div>
        </div>
      </div>

      <div className="home-info-tiles">
        <InfoTile title="Customer Support" icon={<HeadsetIcon />}>
          <p>📞 01785988734</p>
          <p>📞 01792649361</p>
        </InfoTile>
        <InfoTile title="Support Time" icon={<ClockIcon />}>
          <p>10.00 AM - 6.00 PM (Without Friday &amp; Govt. Holiday)</p>
        </InfoTile>
        <InfoTile title="Masking SMS" icon={<ChatIcon />}>
          <p>Total Masking SMS Balance : 0</p>
        </InfoTile>
        <InfoTile title="Tutorial" icon={<TvIcon />}>
          <p>Description</p>
        </InfoTile>
      </div>
    </MainLayout>
  );
}

function StatBox({ value, label }) {
  return (
    <div className="home-stat-box">
      <span className="home-stat-value">{value}</span>
      <span className="home-stat-label">{label}</span>
    </div>
  );
}

function InfoTile({ title, icon, children }) {
  return (
    <div className="home-info-tile">
      <div className="home-info-tile-title">{title}</div>
      <div className="home-info-tile-icon">{icon}</div>
      {children}
    </div>
  );
}

function iconProps() {
  return { width: 26, height: 26, viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: 1.6, strokeLinecap: "round", strokeLinejoin: "round" };
}

function HeadsetIcon() {
  return (
    <svg {...iconProps()}>
      <path d="M4 13v-1a8 8 0 0116 0v1" />
      <rect x="3" y="13" width="4" height="6" rx="1.5" />
      <rect x="17" y="13" width="4" height="6" rx="1.5" />
      <path d="M19 19v1a2 2 0 01-2 2h-3" />
    </svg>
  );
}

function ClockIcon() {
  return (
    <svg {...iconProps()}>
      <circle cx="12" cy="12" r="9" />
      <path d="M12 7v5l3.5 2" />
    </svg>
  );
}

function ChatIcon() {
  return (
    <svg {...iconProps()}>
      <path d="M4 5h16v11H8l-4 4V5z" />
      <path d="M8 9h8M8 12h5" />
    </svg>
  );
}

function TvIcon() {
  return (
    <svg {...iconProps()}>
      <rect x="3" y="5" width="18" height="12" rx="1.5" />
      <path d="M8 21h8M12 17v4" />
    </svg>
  );
}
